import { useAuthToken } from "@convex-dev/auth/react";

/**
 * The signed-in account id, as soon as there is a token.
 *
 * Every private cache on this device is partitioned by account, and the
 * partition used to come from a `current-owner` marker written after a profile
 * query resolved. A session that expires or is revoked without `secureSignOut`
 * leaves that marker behind, so the next person to sign in on the same browser
 * read and wrote under the previous account's partition until the marker caught
 * up -- and the window where it had not is exactly when a gate is deciding what
 * to render.
 *
 * The token carries the answer already. Convex Auth puts `<userId>|<sessionId>`
 * in `sub`, and it is available before any query returns, which is what makes
 * the cache usable during an outage rather than only after the backend replies.
 *
 * Deliberately **not** verified here. Nothing is authorized on this value: the
 * server checks the token on every call, and a forged one would only partition
 * a local cache into a compartment holding nothing.
 */
export function useAuthenticatedOwner(): string | null {
  const token = useAuthToken();
  return token === null ? null : subjectUserId(token);
}

/** The user id in a JWT's `sub`, or null if it is not shaped like one. */
export function subjectUserId(token: string): string | null {
  const payload = token.split(".")[1];
  if (payload === undefined) return null;
  try {
    const json: unknown = JSON.parse(
      // `atob` reads standard base64; a JWT is base64url.
      atob(payload.replace(/-/gu, "+").replace(/_/gu, "/")),
    );
    if (typeof json !== "object" || json === null) return null;
    const subject = (json as { sub?: unknown }).sub;
    if (typeof subject !== "string") return null;
    const [userId] = subject.split("|");
    return userId === undefined || userId === "" ? null : userId;
  } catch {
    return null;
  }
}
